/**
 * V154 — customer-facing banner that surfaces upstream AI / media
 * provider outages. Niels asked for this after the 2026-05-22
 * Anthropic Haiku overload that stretched for hours with no visible
 * signal to customers — they sat there waiting for posts to appear
 * with no indication that the AI provider was the bottleneck.
 *
 * Polls the public /api/public/system/status endpoint every 30s. No
 * JWT required — banner renders on the login / pricing pages too so
 * a new customer signing up during an outage sees "we know, working
 * on it" instead of "did I do something wrong".
 *
 * Banner clears automatically the moment any single successful
 * upstream call lands — the backend `ProviderStatusService` flips
 * status back to "ok" on the next reportSuccess and our 30s poll
 * picks it up.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { systemStatusApi, type ProviderStatusEntry } from '@/lib/api';

export function ProviderStatusBanner() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['system-status'],
    queryFn: systemStatusApi.status,
    refetchInterval: 30_000,
    // Treat fetch failures as silently-ok — we'd rather not banner on
    // a network blip than render a confusing "everything down" state
    // when the customer's wifi dropped.
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (!data?.degraded) return null;
  const degraded = data.providers.filter((p) => p.status === 'degraded');
  return (
    <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-amber-700 dark:text-amber-300">
      <div className="mx-auto flex max-w-7xl items-start gap-3 text-sm">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {t(
              'providerStatus.degradedTitle',
              'AI generation is currently delayed — upstream provider issue',
            )}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-amber-700/80 dark:text-amber-300/80">
            {t(
              'providerStatus.degradedBody',
              "We're seeing errors from our AI provider. Your posts will land in Review as soon as the provider recovers — no action needed.",
            )}{' '}
            {degraded.length > 0 && (
              <span className="text-amber-700/60 dark:text-amber-300/60">
                ({describeDegraded(degraded)})
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact human-readable list of affected services. Backend keys are
 * shaped `anthropic:haiku` / `anthropic:sonnet` / `fal_ai` / etc.
 */
function describeDegraded(entries: ProviderStatusEntry[]): string {
  return entries
    .map((p) => {
      const label = friendlyProviderLabel(p.provider);
      return p.reason ? `${label}: ${p.reason}` : label;
    })
    .join(', ');
}

function friendlyProviderLabel(key: string): string {
  const [provider, model] = key.split(':');
  if (provider === 'anthropic') {
    if (!model) return 'Claude';
    return `Claude ${model.charAt(0).toUpperCase()}${model.slice(1)}`;
  }
  if (provider === 'fal_ai') return 'fal.ai';
  if (provider === 'perplexity') return 'Perplexity';
  if (provider === 'gemini') return 'Gemini';
  return key;
}
